/**
 * Product-Centric Breadcrumbs (replaces history-based navigation trail)
 *
 * Shows logical product-centric paths rather than tracking navigation history.
 *
 * Behavior:
 * - Home page: No breadcrumbs
 * - Collection pages: Home >> [Collection]
 * - Product pages: Home >> [Context Collection OR Primary Category] >> [Product]
 * - Other pages (Search, Cart, etc.): Home >> [Page Name]
 *
 * Product Context Logic:
 * - When visiting a collection, store it as "breadcrumb context"
 * - When visiting a product:
 *   - If context exists AND product belongs to that collection → keep context
 *   - Otherwise → use product's primary category
 * - When visiting non-product/collection pages → clear context
 *
 * Tab-scoped storage ensures each browser tab has independent context.
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    storageKeyPrefix: 'zenware_breadcrumb_',
    tabIdKey: 'zenware_breadcrumb_tab_id',
    homePath: '/',
    version: 1,
    debug: false, // Set to true to enable console logging
    // Pages to exclude from breadcrumbs entirely
    excludedPatterns: [
      /^\/checkout/,
      /^\/account/,
      /^\/404/,
      /^\/challenge/,
    ],
    // Collection handles considered "primary categories" (in priority order)
    // These are used as fallback when product isn't in the current context
    primaryCategoryHandles: [
      'plates',
      'bowls',
      'teapots-cups',
      'sake-barware',
      'chopsticks',
      'dinnerware-set',
      'home-decor',
      'mix-match',
    ],
    // Title truncation settings
    titleMaxLength: {
      mobile: 40,
      desktop: 60,
    },
    // Mobile breakpoint
    mobileBreakpoint: 749,
  };

  // Debug logging helper
  function log(...args) {
    if (CONFIG.debug) {
      console.log('[Breadcrumb]', ...args);
    }
  }

  /**
   * Generate a unique Tab ID
   */
  function generateTabId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Get or create the Tab ID for this browsing context
   */
  function getTabId() {
    try {
      let tabId = sessionStorage.getItem(CONFIG.tabIdKey);
      if (!tabId) {
        tabId = generateTabId();
        sessionStorage.setItem(CONFIG.tabIdKey, tabId);
        log('Generated new Tab ID:', tabId);
      }
      return tabId;
    } catch (e) {
      console.warn('Breadcrumb: sessionStorage not available', e);
      return null;
    }
  }

  /**
   * Get the storage key for the current tab
   */
  function getStorageKey() {
    const tabId = getTabId();
    return tabId ? CONFIG.storageKeyPrefix + tabId : CONFIG.storageKeyPrefix + 'shared';
  }

  // Storage abstraction (sessionStorage for tab isolation)
  const Storage = {
    _storage: null,

    _getStorage() {
      if (this._storage !== null) return this._storage;
      try {
        const testKey = '__storage_test__';
        sessionStorage.setItem(testKey, testKey);
        sessionStorage.removeItem(testKey);
        this._storage = sessionStorage;
      } catch (e) {
        this._storage = false;
      }
      return this._storage;
    },

    get(key) {
      const storage = this._getStorage();
      if (!storage) return null;
      try {
        const item = storage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (e) {
        return null;
      }
    },

    set(key, value) {
      const storage = this._getStorage();
      if (!storage) return false;
      try {
        storage.setItem(key, JSON.stringify(value));
        return true;
      } catch (e) {
        return false;
      }
    },

    remove(key) {
      const storage = this._getStorage();
      if (!storage) return;
      try {
        storage.removeItem(key);
      } catch (e) {
        // Ignore
      }
    },
  };

  /**
   * Context data structure:
   * {
   *   version: number,
   *   collection: { handle: string, title: string, url: string } | null
   * }
   */

  /**
   * Get the stored breadcrumb context
   */
  function getContext() {
    const key = getStorageKey();
    const data = Storage.get(key);
    if (!data || data.version !== CONFIG.version) {
      return null;
    }
    return data.collection;
  }

  /**
   * Set the breadcrumb context (collection)
   */
  function setContext(collection) {
    const key = getStorageKey();
    Storage.set(key, {
      version: CONFIG.version,
      collection: collection,
    });
    log('Set context:', collection);
  }

  /**
   * Clear the breadcrumb context
   */
  function clearContext() {
    const key = getStorageKey();
    Storage.remove(key);
    log('Cleared context');
  }

  /**
   * Normalize URL path
   */
  function normalizePath(url) {
    if (!url) return '/';
    let path = url.split('?')[0].split('#')[0];
    if (path === '' || path === '/') return '/';
    return path.replace(/\/$/, '');
  }

  /**
   * Check if current page is home
   */
  function isHomePage(path) {
    const normalized = normalizePath(path);
    return normalized === CONFIG.homePath || normalized === '';
  }

  /**
   * Check if page should be excluded from breadcrumbs
   */
  function isExcludedPage(path) {
    const normalizedPath = normalizePath(path);
    return CONFIG.excludedPatterns.some((pattern) => pattern.test(normalizedPath));
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Decode HTML entities (e.g., &amp; → &)
   * Used to decode titles that may contain HTML entities from Shopify
   */
  function decodeHtmlEntities(str) {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  }

  /**
   * Check if we're on mobile
   */
  function isMobile() {
    return window.innerWidth <= CONFIG.mobileBreakpoint;
  }

  /**
   * Truncate a title to a maximum length
   */
  function truncateTitle(title, maxLength) {
    if (!title || title.length <= maxLength) {
      return { truncated: title, wasTruncated: false };
    }
    let truncateAt = maxLength;
    const lastSpace = title.lastIndexOf(' ', maxLength - 3);
    if (lastSpace > maxLength * 0.5) {
      truncateAt = lastSpace;
    } else {
      truncateAt = maxLength - 3;
    }
    return {
      truncated: title.substring(0, truncateAt).trim() + '...',
      wasTruncated: true,
    };
  }

  /**
   * Find the primary category for a product
   * Returns the first collection that matches our category list
   */
  function findPrimaryCategory(productCollections) {
    if (!productCollections || !Array.isArray(productCollections)) {
      return null;
    }

    // First, try to find a match in priority category handles
    for (const priorityHandle of CONFIG.primaryCategoryHandles) {
      const match = productCollections.find((c) => c.handle === priorityHandle);
      if (match) {
        return match;
      }
    }

    // Fallback: return the first collection (if any)
    return productCollections.length > 0 ? productCollections[0] : null;
  }

  /**
   * Check if product belongs to a collection
   */
  function productBelongsToCollection(productCollections, collectionHandle) {
    if (!productCollections || !collectionHandle) return false;
    return productCollections.some((c) => c.handle === collectionHandle);
  }

  /**
   * Build the breadcrumb trail based on page data
   * Returns an array of { title, url, isCurrent }
   */
  function buildBreadcrumbTrail(pageData) {
    const trail = [];

    // Always start with Home
    trail.push({
      title: 'Home',
      url: '/',
      isCurrent: false,
    });

    const path = normalizePath(pageData.url);

    // Handle different page types
    if (pageData.pageType === 'collection') {
      // Collection page: Home >> Collection
      const collectionInfo = pageData.collection;
      if (collectionInfo) {
        // Store this collection as the new context
        setContext({
          handle: collectionInfo.handle,
          title: decodeHtmlEntities(collectionInfo.title),
          url: '/collections/' + collectionInfo.handle,
        });

        trail.push({
          title: decodeHtmlEntities(collectionInfo.title),
          url: '/collections/' + collectionInfo.handle,
          isCurrent: true,
        });
      }
    } else if (pageData.pageType === 'product') {
      // Product page: Home >> [Context/Category] >> Product
      const productCollections = pageData.productCollections || [];
      const currentContext = getContext();

      let middleStep = null;

      // Check if current context is valid for this product
      if (currentContext && productBelongsToCollection(productCollections, currentContext.handle)) {
        // Product is in the current context collection - use it
        middleStep = {
          title: currentContext.title,
          url: currentContext.url,
          isCurrent: false,
        };
        log('Using context collection:', currentContext.handle);
      } else {
        // Product is NOT in current context - find primary category
        const primaryCategory = findPrimaryCategory(productCollections);
        if (primaryCategory) {
          middleStep = {
            title: decodeHtmlEntities(primaryCategory.title),
            url: '/collections/' + primaryCategory.handle,
            isCurrent: false,
          };
          // Update context to this category
          setContext({
            handle: primaryCategory.handle,
            title: decodeHtmlEntities(primaryCategory.title),
            url: '/collections/' + primaryCategory.handle,
          });
          log('Using primary category:', primaryCategory.handle);
        }
      }

      if (middleStep) {
        trail.push(middleStep);
      }

      // Add the product
      trail.push({
        title: decodeHtmlEntities(pageData.title),
        url: pageData.url,
        isCurrent: true,
      });
    } else if (pageData.pageType === 'search') {
      // Search page: Home >> Search (or "Search: query")
      clearContext();

      let searchTitle = 'Search';
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('q');
      if (query && query.trim()) {
        searchTitle = 'Search: ' + query.trim();
      }

      trail.push({
        title: searchTitle,
        url: path + window.location.search,
        isCurrent: true,
      });
    } else {
      // Other pages (cart, about, etc.): Home >> Page Title
      // Clear context for non-product/collection pages
      clearContext();

      trail.push({
        title: decodeHtmlEntities(pageData.title),
        url: path,
        isCurrent: true,
      });
    }

    return trail;
  }

  /**
   * Render the breadcrumb trail to the DOM
   */
  function renderBreadcrumbs(trail) {
    const listEl = document.getElementById('navigation-trail-list');
    if (!listEl) return;

    const navEl = document.getElementById('navigation-trail');
    const mobile = isMobile();
    const maxTitleLength = mobile ? CONFIG.titleMaxLength.mobile : CONFIG.titleMaxLength.desktop;

    if (CONFIG.debug && navEl) {
      const tabId = getTabId();
      if (tabId) {
        navEl.setAttribute('data-tab-id', tabId);
      }
    }

    let html = '';

    // Mobile: show "← Back to [Previous]" when trail > 2 items
    if (mobile && trail.length > 2) {
      // Find the previous item (not current)
      const prevItem = trail[trail.length - 2];
      if (prevItem && !prevItem.isCurrent) {
        const { truncated, wasTruncated } = truncateTitle(prevItem.title, maxTitleLength);
        const titleAttr = wasTruncated ? ` title="${escapeHtml(prevItem.title)}"` : '';

        html = `
          <li class="navigation-trail__item navigation-trail__item--mobile-back">
            <a href="${escapeHtml(prevItem.url)}" class="navigation-trail__link navigation-trail__link--back"${titleAttr}>
              <span class="navigation-trail__back-arrow" aria-hidden="true">←</span>
              Back to ${escapeHtml(truncated)}
            </a>
          </li>
        `;

        if (navEl) {
          navEl.classList.add('navigation-trail--mobile-collapsed');
        }
      }
    } else {
      // Desktop view or short trail
      if (navEl) {
        navEl.classList.remove('navigation-trail--mobile-collapsed');
      }

      trail.forEach((item, index) => {
        if (item.isCurrent) {
          // Current page - no link
          html += `
            <li class="navigation-trail__item">
              <span class="navigation-trail__current" aria-current="page">${escapeHtml(item.title)}</span>
            </li>
          `;
        } else {
          // Linkable item
          const { truncated, wasTruncated } = truncateTitle(item.title, maxTitleLength);
          const titleAttr = wasTruncated ? ` title="${escapeHtml(item.title)}"` : '';

          html += `
            <li class="navigation-trail__item">
              <a href="${escapeHtml(item.url)}" class="navigation-trail__link"${titleAttr}>${escapeHtml(truncated)}</a>
            </li>
          `;
        }
      });
    }

    listEl.innerHTML = html;
  }

  /**
   * Initialize the breadcrumb system
   */
  function init() {
    const pageData = window.__navTrailPageData;
    if (!pageData) {
      console.warn('Breadcrumb: No page data found');
      return;
    }

    log('Initializing with Tab ID:', getTabId());
    log('Page:', pageData.url, 'Type:', pageData.pageType);

    const navEl = document.getElementById('navigation-trail');

    // Home page - no breadcrumbs
    if (isHomePage(pageData.url)) {
      clearContext();
      log('On home page, no breadcrumbs');
      return;
    }

    // Excluded pages - hide breadcrumbs
    if (isExcludedPage(pageData.url)) {
      if (navEl) {
        navEl.style.display = 'none';
      }
      log('On excluded page, hiding breadcrumbs');
      return;
    }

    if (!navEl) {
      return;
    }

    // Build and render the breadcrumb trail
    const trail = buildBreadcrumbTrail(pageData);
    renderBreadcrumbs(trail);

    // Handle window resize for responsive rendering
    let resizeTimeout;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        renderBreadcrumbs(trail);
      }, 150);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
